/**
 * HeaderSystemStrip.tsx — MER Algo Footprints v9.2.0
 *
 * Phase 2: Global Trading Header — Row 3 (System Strip, 42px).
 *
 * Logic extracted from features/status/SystemStatusBar.tsx (which currently
 * lives in dead code under App.tsx) and adapted to consume the
 * useGlobalTradingContext VM. NO new business logic — purely a re-render
 * of existing telemetry sources.
 *
 * Fields (left → right):
 *   data source · transport · polling state · interval · fetch age ·
 *   AI mode · AI refresh timer · market OPEN/CLOSED · event trace dropdown
 *
 * The event trace dropdown reads features/status/systemEventLog.ts directly,
 * which is the same source SystemStatusBar uses.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { GlobalTradingContextVM } from '../../hooks/useGlobalTradingContext';
import {
  getEventLog,
  subscribeEventLog,
  SystemEvent,
  SystemEventCategory,
} from '../../features/status/systemEventLog';
import { HeaderStatusPill } from './HeaderStatusPill';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ageStr(ts: number | null, now: number): string {
  if (!ts) return '—';
  const s = Math.max(0, Math.floor((now - ts) / 1000));
  if (s < 2)    return 'just now';
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function fetchStaleness(
  ts: number | null,
  now: number,
): 'green' | 'yellow' | 'red' | 'muted' {
  if (!ts) return 'muted';
  const ms = now - ts;
  if (ms < 60_000)  return 'green';
  if (ms < 300_000) return 'yellow';
  return 'red';
}

function aiTone(mode: GlobalTradingContextVM['aiStatus'], inFlight: boolean):
  'green' | 'red' | 'purple' | 'muted' {
  if (inFlight) return 'purple';
  if (mode === 'ai_unavailable')   return 'red';
  if (mode === 'ai_assisted')      return 'green';
  return 'muted';   // deterministic_only
}

function aiLabel(mode: GlobalTradingContextVM['aiStatus'], inFlight: boolean): string {
  if (inFlight) return 'AI WORKING';
  switch (mode) {
    case 'ai_assisted':       return 'AI ON';
    case 'deterministic_only':return 'AI OFF';
    case 'ai_unavailable':    return 'AI ERR';
    default:                  return 'AI —';
  }
}

function marketTone(s: GlobalTradingContextVM['marketStatus']):
  'green' | 'yellow' | 'muted' {
  if (s === 'open')     return 'green';
  if (s === 'extended') return 'yellow';
  return 'muted';
}

function marketLabel(s: GlobalTradingContextVM['marketStatus']): string {
  if (s === 'open')     return 'MARKET OPEN';
  if (s === 'extended') return 'EXT HRS';
  return 'CLOSED';
}

const CATEGORY_COLORS: Record<SystemEventCategory, string> = {
  market:    'var(--mer-muted)',
  detection: 'var(--mer-green)',
  ai:        'var(--mer-purple)',
  thesis:    'var(--mer-yellow)',
  ranking:   'var(--mer-text)',
  alert:     'var(--mer-red)',
  feed:      'var(--mer-label)',
};

// ─── Strip cell ───────────────────────────────────────────────────────────────

function Cell({
  label, children, title,
}: { label: string; children: React.ReactNode; title?: string }) {
  return (
    <div
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '0 12px',
        borderRight: '1px solid var(--mer-border)',
        height: '100%', whiteSpace: 'nowrap', flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize: 9, letterSpacing: 1, color: 'var(--mer-label)',
          textTransform: 'uppercase', fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 11, color: 'var(--mer-text)', fontVariantNumeric: 'tabular-nums' }}>
        {children}
      </span>
    </div>
  );
}

// ─── Event log dropdown ──────────────────────────────────────────────────────

function EventTraceDropdown({ now }: { now: number }) {
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const ref = useRef<HTMLDivElement | null>(null);

  // Subscribe to event log mutations
  useEffect(() => {
    const unsub = subscribeEventLog(() => force(n => n + 1));
    return unsub;
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onDoc(ev: MouseEvent) {
      if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const events: SystemEvent[] = useMemo(() => getEventLog(), [now]);

  const toggle = useCallback(() => setOpen(o => !o), []);

  return (
    <div ref={ref} style={{ position: 'relative', height: '100%', flexShrink: 0 }}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={{
          height: '100%',
          padding: '0 14px',
          background: open ? 'rgba(166,108,255,0.08)' : 'transparent',
          border: 'none',
          borderLeft: '1px solid var(--mer-border)',
          color: 'var(--mer-text)',
          cursor: 'pointer',
          fontSize: 10,
          letterSpacing: 1,
          textTransform: 'uppercase',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ color: 'var(--mer-label)' }}>EVENTS</span>
        <span style={{ color: 'var(--mer-text)' }}>{events.length}</span>
        <span aria-hidden="true" style={{ fontSize: 10, color: 'var(--mer-muted)' }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 2,
            minWidth: 360,
            maxHeight: 400,
            overflowY: 'auto',
            background: 'var(--mer-bg-strip)',
            border: '1px solid var(--mer-border)',
            borderRadius: 4,
            zIndex: 1000,
            padding: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
        >
          {events.length === 0 ? (
            <div style={{ padding: 10, color: 'var(--mer-muted)', fontSize: 11 }}>
              No events recorded yet.
            </div>
          ) : (
            events.slice().reverse().map(ev => (
              <div
                key={ev.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  padding: '6px 8px',
                  borderBottom: '1px solid var(--mer-border)',
                  fontSize: 11,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    style={{
                      fontSize: 9, letterSpacing: 1, textTransform: 'uppercase',
                      color: CATEGORY_COLORS[ev.category],
                      fontWeight: 600,
                      minWidth: 64,
                    }}
                  >
                    {ev.category}
                  </span>
                  <span style={{ color: 'var(--mer-muted)', fontSize: 10 }}>
                    {new Date(ev.ts).toLocaleTimeString('en-US', { hour12: false })}
                  </span>
                  {ev.symbol && (
                    <span style={{ color: 'var(--mer-yellow)', fontWeight: 600 }}>
                      {ev.symbol}
                    </span>
                  )}
                </div>
                <div style={{ color: 'var(--mer-text)' }}>{ev.message}</div>
                {ev.detail && (
                  <div style={{ color: 'var(--mer-muted)', fontSize: 10 }}>{ev.detail}</div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main strip ──────────────────────────────────────────────────────────────

export interface HeaderSystemStripProps {
  vm: GlobalTradingContextVM;
}

export function HeaderSystemStrip({ vm }: HeaderSystemStripProps) {
  const fetchAgeTone = fetchStaleness(vm.lastFetchTs, vm.currentTime);
  const aiTimer = vm.aiLastSuccessAt
    ? ageStr(vm.aiLastSuccessAt, vm.currentTime)
    : '—';
  const fetchAgeText = vm.lastFetchTs ? ageStr(vm.lastFetchTs, vm.currentTime) : '—';
  const candleAgeText = vm.lastTickTs ? ageStr(vm.lastTickTs, vm.currentTime) : '—';

  return (
    <div
      className="mer-system-strip"
      style={{
        background: 'var(--mer-bg-strip)',
        height: 42,
        display: 'flex',
        alignItems: 'center',
        borderTop: '1px solid var(--mer-border)',
        fontFamily: "'Courier New', monospace",
        overflow: 'hidden',
      }}
    >
      {/* Inner scroller for mobile horizontal scroll */}
      <div
        className="mer-system-strip-inner"
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
        }}
      >
        <Cell label="Source" title="Active market data provider">
          {vm.dataMode}
        </Cell>
        {/*
         * Transport and Poll cells hidden until wsStatus is wired to the
         * backend's Redis provider-status stream. Currently wsStatus is
         * declared in useMarketData but never assigned, so these would
         * show false 'disconnected' / 'HTTP' regardless of actual WS
         * health. Restore as part of learning-system Phase 2 stream wiring.
         * See follow-up #2 in the Phase 2 close-out notes.
         */}
        <Cell label="Interval" title="Poll interval">
          {vm.pollIntervalSec}s
        </Cell>
        <Cell label="TF/HZN" title="Bar timeframe / trader horizon">
          {/* Backlog #1: chart bar resolution + trader horizon. Bar intervals
              are stored as '1min'/'5min'/'15min' -- shortened for display.
              Horizon is stored lowercase -- uppercased for visual emphasis. */}
          TF: {(vm.barInterval || '—').replace(/min$/, 'm')}
          {' • '}
          HZN: {(vm.horizon || '—').toUpperCase()}
        </Cell>
        <Cell label="Fetch" title="Age of last REST fetch">
          <span style={{ color: `var(--mer-${fetchAgeTone === 'green' ? 'green' : fetchAgeTone === 'yellow' ? 'yellow' : fetchAgeTone === 'red' ? 'red' : 'muted'})` }}>
            {fetchAgeText}
          </span>
        </Cell>
        <Cell label="Tick" title="Age of last WS tick">
          {candleAgeText}
        </Cell>
        <Cell label="AI" title="AI orchestrator state">
          <HeaderStatusPill
            tone={aiTone(vm.aiStatus, vm.aiInFlight)}
            size="sm"
            label={aiLabel(vm.aiStatus, vm.aiInFlight)}
            dot
          />
        </Cell>
        <Cell label="AI Refresh" title="Time since last successful AI thesis">
          {aiTimer}
        </Cell>
        <Cell label="Market" title="Market session">
          <HeaderStatusPill
            tone={marketTone(vm.marketStatus)}
            size="sm"
            label={marketLabel(vm.marketStatus)}
            dot
          />
        </Cell>
        {vm.error && (
          <Cell label="Reason" title="Last error">
            <span style={{ color: 'var(--mer-red)' }}>{vm.error}</span>
          </Cell>
        )}
      </div>

      {/* Pinned right — event trace dropdown */}
      <EventTraceDropdown now={vm.currentTime} />
    </div>
  );
}
